"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import React, { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { CaretLeft, CaretRight, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "@/navigation";
import { cn } from "@/lib/utils";
import "swiper/css";
import "swiper/css/navigation";

// 数据卡片类型
export interface StatCard {
	valueKey: string;
	labelKey: string;
	variant: "light" | "primary"; // light: 白底绿字, primary: 绿底白字
}

// 轮播案例数据配置
export interface StoryData {
	id: number;
	categoryKey: string;
	titleKey: string;
	descKey: string;
	stats: StatCard[];
	image: string;
	link: string;
}

// 默认案例数据配置
const DEFAULT_STORIES_DATA: StoryData[] = [
	{
		id: 1,
		categoryKey: "story1Category",
		titleKey: "story1Title",
		descKey: "story1Desc",
		stats: [
			{ valueKey: "story1Stat1Value", labelKey: "story1Stat1Label", variant: "light" },
			{ valueKey: "story1Stat2Value", labelKey: "story1Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/consumer-electronics.webp",
		link: "/cases",
	},
	{
		id: 2,
		categoryKey: "story2Category",
		titleKey: "story2Title",
		descKey: "story2Desc",
		stats: [
			{ valueKey: "story2Stat1Value", labelKey: "story2Stat1Label", variant: "light" },
			{ valueKey: "story2Stat2Value", labelKey: "story2Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/aerospace.webp",
		link: "/cases",
	},
	{
		id: 3,
		categoryKey: "story3Category",
		titleKey: "story3Title",
		descKey: "story3Desc",
		stats: [
			{ valueKey: "story3Stat1Value", labelKey: "story3Stat1Label", variant: "light" },
			{ valueKey: "story3Stat2Value", labelKey: "story3Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/robotics.webp",
		link: "/cases",
	},
	{
		id: 4,
		categoryKey: "story4Category",
		titleKey: "story4Title",
		descKey: "story4Desc",
		stats: [
			{ valueKey: "story4Stat1Value", labelKey: "story4Stat1Label", variant: "light" },
			{ valueKey: "story4Stat2Value", labelKey: "story4Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/automotive.webp",
		link: "/cases",
	},
	{
		id: 5,
		categoryKey: "story5Category",
		titleKey: "story5Title",
		descKey: "story5Desc",
		stats: [
			{ valueKey: "story5Stat1Value", labelKey: "story5Stat1Label", variant: "light" },
			{ valueKey: "story5Stat2Value", labelKey: "story5Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/industrial-equipment.webp",
		link: "/cases",
	},
];

// Gear Machining 案例数据配置
export const GEAR_STORIES_DATA: StoryData[] = [
	{
		id: 1,
		categoryKey: "story1Category",
		titleKey: "story1Title",
		descKey: "story1Desc",
		stats: [
			{ valueKey: "story1Stat1Value", labelKey: "story1Stat1Label", variant: "light" },
			{ valueKey: "story1Stat2Value", labelKey: "story1Stat2Label", variant: "light" },
			{ valueKey: "story1Stat3Value", labelKey: "story1Stat3Label", variant: "light" },
		],
		image: "/image/new-version/machining/gear-machining/case/aerospace.webp",
		link: "/cases",
	},
	{
		id: 2,
		categoryKey: "story2Category",
		titleKey: "story2Title",
		descKey: "story2Desc",
		stats: [
			{ valueKey: "story2Stat1Value", labelKey: "story2Stat1Label", variant: "light" },
			{ valueKey: "story2Stat2Value", labelKey: "story2Stat2Label", variant: "light" },
			{ valueKey: "story2Stat3Value", labelKey: "story2Stat3Label", variant: "light" },
		],
		image: "/image/new-version/machining/gear-machining/case/industrial-machinery.webp",
		link: "/cases",
	},
	{
		id: 3,
		categoryKey: "story3Category",
		titleKey: "story3Title",
		descKey: "story3Desc",
		stats: [
			{ valueKey: "story3Stat1Value", labelKey: "story3Stat1Label", variant: "light" },
			{ valueKey: "story3Stat2Value", labelKey: "story3Stat2Label", variant: "light" },
			{ valueKey: "story3Stat3Value", labelKey: "story3Stat3Label", variant: "light" },
		],
		image: "/image/new-version/machining/gear-machining/case/robotic-automation.webp",
		link: "/cases",
	},
	{
		id: 4,
		categoryKey: "story4Category",
		titleKey: "story4Title",
		descKey: "story4Desc",
		stats: [
			{ valueKey: "story4Stat1Value", labelKey: "story4Stat1Label", variant: "light" },
			{ valueKey: "story4Stat2Value", labelKey: "story4Stat2Label", variant: "light" },
			{ valueKey: "story4Stat3Value", labelKey: "story4Stat3Label", variant: "light" },
		],
		image: "/image/new-version/machining/gear-machining/case/automotive​.webp",
		link: "/cases",
	},
	{
		id: 5,
		categoryKey: "story5Category",
		titleKey: "story5Title",
		descKey: "story5Desc",
		stats: [
			{ valueKey: "story5Stat1Value", labelKey: "story5Stat1Label", variant: "light" },
			{ valueKey: "story5Stat2Value", labelKey: "story5Stat2Label", variant: "light" },
			{ valueKey: "story5Stat3Value", labelKey: "story5Stat3Label", variant: "light" },
		],
		image: "/image/new-version/machining/gear-machining/case/medical-devices​.webp",
		link: "/cases",
	},
];

// 数据卡片组件
function StatCardItem({ stat, t }: { stat: StatCard; t: (key: string) => string }) {
	const isLight = stat.variant === "light";

	return (
		<div
			className={`flex max-w-[205px] flex-col justify-center rounded-lg px-4 py-3 sm:px-5 sm:py-4 ${
				isLight ? "border-2 border-themePrimary400 bg-white" : "bg-themePrimary400"
			}`}
		>
			<div
				className={`mb-2 text-center text-xl font-bold sm:text-2xl md:text-[28px] ${
					isLight ? "text-black" : "text-white"
				}`}
			>
				{t(stat.valueKey)}
			</div>
			<div className={`text-center text-sm ${isLight ? "text-black" : "text-white"}`}>{t(stat.labelKey)}</div>
		</div>
	);
}

export interface SuccessStoryProps {
	/** 翻译命名空间 */
	translationNamespace?: string;
	/** 案例数据 */
	stories?: StoryData[];
	/** 是否左右各占一半（默认左侧 45%、右侧 55%） */
	equalSplit?: boolean;
	/** 是否强制 stats 在桌面端单行展示 */
	forceSingleRowStats?: boolean;
	className?: string;
}

export default function SuccessStory({
	translationNamespace = "successStory",
	stories = DEFAULT_STORIES_DATA,
	equalSplit = false,
	forceSingleRowStats = false,
	className,
}: SuccessStoryProps) {
	const t = useTranslations(translationNamespace);
	const swiperRef = useRef<SwiperType | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	const handleSlideChange = (swiper: SwiperType) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	return (
		<section className={cn("bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("description")}</p>
				</div>

				{/* 轮播区域：整体缩窄并居中，箭头与内容留距、与其它模块内容对齐 */}
				<div className="relative w-full">
					{/* 左右箭头：仅图标无边框，默认绿色 hover 变黑，与卡片留距 */}
					<button
						onClick={() => swiperRef.current?.slidePrev()}
						disabled={isBeginning}
						className={`group absolute left-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center transition-colors md:h-12 md:w-12 ${
							isBeginning ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100"
						}`}
						aria-label="Previous slide"
					>
						<CaretLeft
							size={28}
							weight="bold"
							className="text-themePrimary400 transition-colors group-hover:text-black md:size-8"
						/>
					</button>

					<button
						onClick={() => swiperRef.current?.slideNext()}
						disabled={isEnd}
						className={`group absolute right-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center transition-colors md:h-12 md:w-12 ${
							isEnd ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100"
						}`}
						aria-label="Next slide"
					>
						<CaretRight
							size={28}
							weight="bold"
							className="text-themePrimary400 transition-colors group-hover:text-black md:size-8"
						/>
					</button>

					{/* 轮播内容与箭头留距 */}
					<div className="container">
						<Swiper
							modules={[Navigation, Autoplay]}
							spaceBetween={24}
							slidesPerView={1}
							onSwiper={(swiper) => {
								swiperRef.current = swiper;
							}}
							onSlideChange={handleSlideChange}
							className="overflow-hidden"
							autoHeight
							// autoplay={{
							// 	delay: 5000,
							// 	disableOnInteraction: false,
							// }}
						>
							{stories.map((story) => (
								<SwiperSlide key={story.id}>
									<article className="overflow-hidden rounded-xl border-4 border-[#e5e5e5] bg-white px-4 py-8 shadow-sm transition-shadow duration-300 sm:border-[6px] sm:px-6 sm:py-10 md:border-[8px] md:px-8 md:py-12 lg:px-10 lg:py-[50px]">
										<div className="flex flex-col gap-6 sm:gap-8 md:gap-10 lg:flex-row lg:items-stretch">
											{/* 左侧图片区域 */}
											<div
												className={`relative aspect-[4/3] w-full min-w-0 overflow-hidden bg-white py-2 ${
													equalSplit ? "lg:flex-1" : "lg:w-[45%]"
												}`}
											>
												<Image
													src={story.image}
													alt={t(story.titleKey)}
													width={1000}
													height={1000}
													className="h-full object-cover"
													sizes={
														equalSplit ? "(max-width: 1024px) 100vw, 50vw" : "(max-width: 1024px) 100vw, 40vw"
													}
												/>
											</div>

											{/* 右侧内容区域 */}
											<div className={`flex min-w-0 flex-col ${equalSplit ? "lg:flex-1" : "flex-1"}`}>
												{/* 分类标签 */}
												<span className="mb-3 w-fit border-b-[2.5px] border-themePrimary400 text-sm font-semibold text-[#333333] sm:mb-4 sm:text-base">
													{t(story.categoryKey)}
												</span>

												{/* 案例标题 */}
												<h3 className="mb-8 mt-4 text-lg font-bold text-black sm:text-xl md:text-2xl md:!leading-relaxed">
													{t(story.titleKey)}
												</h3>

												{/* 描述文字 */}
												<p className="mb-8 text-sm leading-relaxed text-black md:text-base">
													{t(story.descKey)}
												</p>

												{/* 数据卡片区域 */}
												<div
													className={cn(
														"gap-3 sm:gap-4",
														forceSingleRowStats
															? "grid grid-cols-1 sm:grid-cols-3"
															: "flex flex-wrap",
													)}
												>
													{story.stats.map((stat, idx) => (
														<StatCardItem key={idx} stat={stat} t={t} />
													))}
												</div>

												{/* 链接按钮（支持外链新窗口打开） */}
												{story.link.startsWith("http") ? (
													<a
														href={story.link}
														target="_blank"
														rel="noopener noreferrer"
														className="mt-8 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
													>
														{t("readFullCaseStudy")}
														<ArrowUpRight size={16} weight="bold" />
													</a>
												) : (
													<Link
														href={story.link}
														className="mt-8 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
													>
														{t("readFullCaseStudy")}
														<ArrowUpRight size={16} weight="bold" />
													</Link>
												)}
											</div>
										</div>
									</article>
								</SwiperSlide>
							))}
						</Swiper>
					</div>

					{/* 移动端：底部导航按钮 */}
					<div className="mt-4 flex items-center justify-center gap-4 md:hidden">
						<button
							onClick={() => swiperRef.current?.slidePrev()}
							disabled={isBeginning}
							className={`group flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#2E7D32] transition-all duration-300 ${
								isBeginning
									? "cursor-not-allowed opacity-30"
									: "cursor-pointer hover:bg-[#2E7D32] hover:text-white"
							}`}
							aria-label="Previous slide"
						>
							<CaretLeft size={20} weight="bold" className="text-[#2E7D32] group-hover:text-white" />
						</button>

						{/* 分页指示器 */}
						<div className="flex items-center gap-2">
							{stories.map((_, idx) => (
								<button
									key={idx}
									onClick={() => swiperRef.current?.slideTo(idx)}
									className={`h-2 rounded-full transition-all duration-300 ${
										swiperRef.current?.activeIndex === idx || (!swiperRef.current && idx === 0)
											? "w-6 bg-[#2E7D32]"
											: "w-2 bg-gray-300 hover:bg-gray-400"
									}`}
									aria-label={`Go to slide ${idx + 1}`}
								/>
							))}
						</div>

						<button
							onClick={() => swiperRef.current?.slideNext()}
							disabled={isEnd}
							className={`group flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#2E7D32] transition-all duration-300 ${
								isEnd ? "cursor-not-allowed opacity-30" : "cursor-pointer hover:bg-[#2E7D32] hover:text-white"
							}`}
							aria-label="Next slide"
						>
							<CaretRight size={20} weight="bold" className="text-[#2E7D32] group-hover:text-white" />
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}
